import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { BroadcastService } from '@app/core/service';
import { BrandingConfiguratorService } from '@app/manager/settings/company/services/branding-configurator.service';
import { UserService } from '@app/user';
import { BehaviorSubject, Subscription, distinctUntilChanged, filter, map, share, shareReplay, switchMap, take, tap } from 'rxjs';
import { HeaderItem } from '../../model/header.model';
import { HeaderHttpService } from '../http/http.service';
import { menuItemsFilter } from '../../menu/utils';
import { WindowService } from '@app/shared/services';
import { AccessibilityService } from '@app/tw-shared/services';

@Injectable()
export class HeaderService {
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly headerHttpService = inject(HeaderHttpService);
    private readonly userService = inject(UserService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly brandingConfiguratorService = inject(BrandingConfiguratorService);
    private readonly windowService = inject(WindowService);
    private readonly accessibilityService = inject(AccessibilityService);
    private readonly skillCategoryLoaded = new BehaviorSubject<boolean>(false);

    readonly isVeryLargeFontSize$ = this.accessibilityService.isVeryLargeFontSize$;

    readonly skillCategoryLoaded$ = this.skillCategoryLoaded.asObservable();
    private readonly _searchbarExpanded = new BehaviorSubject<boolean>(false);
    readonly searchbarExpanded$ = this._searchbarExpanded.pipe(distinctUntilChanged(), share());
    readonly subscriptions$: Subscription = new Subscription();

    private readonly _items$ = new BehaviorSubject<HeaderItem[]>([]);
    readonly items$ = this._items$.pipe(
        shareReplay(),
        menuItemsFilter({
            windowService: this.windowService,
            isVeryLargeFontSize$: this.isVeryLargeFontSize$,
        }),
    );
    readonly hasCompanyAdminsInCompany$ = new BehaviorSubject<boolean>(false);
    readonly hasCompanyAdminSupport$ = this.userService.user$.pipe(map((user) => user.company.companyAdminSupport?.enabled));

    availableSkillCategories = [];

    get logoSrc() {
        const logoBase64 = this.brandingConfiguratorService.logoFile;
        const logoUrl = this.brandingConfiguratorService.logoFileUrl;
        const logoFileName = this.brandingConfiguratorService.logoFileName;

        if (this.brandingConfiguratorService.customLogoActive && (logoBase64 || logoUrl)) {
            if (logoBase64) {
                let extension = logoFileName.match(/\.[0-9a-z]+$/i)[0].replace('.', '');
                if (extension === 'svg') {
                    extension += '+xml';
                }
                return this.domSanitizer.bypassSecurityTrustUrl(`data:image/${extension};base64,${logoBase64}`);
            }
        }
        if (this.userService.user.company.external) {
            return this.userService.user.company.logo;
        }
        return `assets/logo/${this.userService.user.company.logo}`;
    }

    init(): void {
        const { enabled: isSelfRegistrationEnabled, version: selfRegistrationVersion } = this.userService.user.company.selfRegistration;

        this.subscriptions$.add(
            this.hasCompanyAdminSupport$
                .pipe(
                    filter(Boolean),
                    take(1),
                    filter(() => (isSelfRegistrationEnabled && selfRegistrationVersion !== 3) || !isSelfRegistrationEnabled),
                    switchMap(() =>
                        this.headerHttpService.getComapnyAdminsHttp().pipe(
                            tap((data) => {
                                if (data && data.length > 0) {
                                    this.hasCompanyAdminsInCompany$.next(true);
                                }
                            }),
                        ),
                    ),
                )
                .subscribe(),
        );

        this.subscriptions$.add(
            this.headerHttpService
                .getHeaderItems()
                .pipe(tap((items) => this._items$.next(items ?? [])))
                .subscribe(),
        );

        this.loadAvailableSkillCategories();

        this.subscriptions$.add(
            this.broadcastService.broadcasts
                .pipe(
                    filter((broadcast) => broadcast.message === 'settings-profile-saved'),
                    tap(() => this.loadAvailableSkillCategories()),
                )
                .subscribe(),
        );
    }

    destroy(): void {
        this.subscriptions$.unsubscribe();
    }

    setSearchbarExpand(expand: boolean) {
        this._searchbarExpanded.next(expand);
    }

    private getComapnyAdmins() {
        this.subscriptions$.add(
            this.headerHttpService
                .getComapnyAdminsHttp()
                .pipe(
                    tap((data) => {
                        if (data && data.length > 0) {
                            this.hasCompanyAdminsInCompany$.next(true);
                        }
                    }),
                )
                .subscribe(),
        );
    }

    private loadAvailableSkillCategories() {
        this.subscriptions$.add(
            this.headerHttpService
                .getAvailableSkills()
                .pipe(
                    tap((data) => {
                        this.availableSkillCategories = data.map((skillCategory) => {
                            if (skillCategory.name === 'Diversity, Equity & Inclusion') {
                                skillCategory.name = 'DEI';
                            }
                            return skillCategory;
                        });
                        this.skillCategoryLoaded.next(true);
                    }),
                )
                .subscribe(),
        );
    }
}
